import React from 'react'

import {
  BenefitParticipationDecision,
  CompanyUserBenefitDetailsResponse,
  MakeOptInOptOutDecisionRequest,
  makeOptInOptOutDecision,
} from 'core'

import { Alert, Button, Card, Col, Row } from 'common/components'
import { formatYear, parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useLoginUserV2 } from 'common/hooks'

import BenefitDetailsSummary from '../../../common/benefitSummary/BenefitSummary'

type OptOutDashboardProps = {
  companyUserBenefitDetails: CompanyUserBenefitDetailsResponse
}
export default function OptOutDashboard({ companyUserBenefitDetails }: OptOutDashboardProps) {
  const { user } = useLoginUserV2()

  const { sendCommand: sendMakeDecision, isLoading: isMakeDecisionLoading } = useCommandRequestV2(
    (request: MakeOptInOptOutDecisionRequest) => makeOptInOptOutDecision(request),
  )

  const onOptIn = async () => {
    const result = await sendMakeDecision({
      planPeriodId: companyUserBenefitDetails.planPeriodId,
      decision: BenefitParticipationDecision.OptedIn,
    })
    if (result.completed) {
      window.location.reload()
    }
  }

  return (
    <>
      <Card className={'opt-out-dashboard-wrap mb-16'}>
        <p>Welcome, {user.firstName}</p>
        <h2 className={'text-bold'}>What’s new for today</h2>

        {companyUserBenefitDetails.isMaxDateToTakeBenefitParticipationDecisionReached && (
          <Alert
            type={'info'}
            message={
              <>
                You have <b>OPTED OUT</b> of your ICHRA for{' '}
                <b>{formatYear(companyUserBenefitDetails.planPeriodStartDate)}</b>. If you have a qualifying life event
                and would like to opt-in to your ICHRA, please contact your benefits administrator.
              </>
            }
          ></Alert>
        )}

        {!companyUserBenefitDetails.isMaxDateToTakeBenefitParticipationDecisionReached &&
          companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision &&
          !companyUserBenefitDetails.hasBenefitStarted && (
            <Alert
              type={'warning'}
              message={
                <Row gutter={16} align={'middle'} justify={'space-between'}>
                  <Col>
                    You have <b>OPTED OUT</b> of your ICHRA benefit (see details below). You have until{' '}
                    <b>{parseZoneFormat(companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision)}</b> to
                    change your decision and opt-in to the ICHRA.
                  </Col>

                  <Col>
                    <Button
                      type={'default'}
                      onClick={onOptIn}
                      loading={isMakeDecisionLoading}
                      disabled={isMakeDecisionLoading}
                    >
                      Opt In
                    </Button>
                  </Col>
                </Row>
              }
            ></Alert>
          )}

        {!companyUserBenefitDetails.isMaxDateToTakeBenefitParticipationDecisionReached &&
          companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision &&
          companyUserBenefitDetails.hasBenefitStarted && (
            <Alert
              type={'warning'}
              message={
                <Row gutter={16} align={'middle'} justify={'space-between'}>
                  <Col>
                    You have <b>OPTED OUT</b> of your ICHRA benefit (see details below).
                  </Col>
                </Row>
              }
            ></Alert>
          )}
      </Card>

      <BenefitDetailsSummary
        planPeriodStartDate={companyUserBenefitDetails.planPeriodStartDate}
        planPeriodEndDate={companyUserBenefitDetails.planPeriodEndDate}
        primaryBenefitContactName={companyUserBenefitDetails.primaryBenefitContact.name}
        primaryBenefitContactEmail={companyUserBenefitDetails.primaryBenefitContact.workEmail}
        familyStatus={companyUserBenefitDetails.familyStatus.name}
        classDetails={companyUserBenefitDetails.classDetails.name}
        expenseType={companyUserBenefitDetails.classDetails.expenseType.name}
        monthlyAllowance={companyUserBenefitDetails.classDetails.monthlyAllowance}
      />
    </>
  )
}
